import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import { Alert, Box, CircularProgress } from '@mui/material';
import { useParams } from 'react-router';

import { AccessDenied } from '@/components/feedback/AccessDenied';
import { useGetApplicationQuery, useGetWorkspaceUsersQuery } from '@/features/apps/api/appsApi';
import type { Application } from '@/types/application';
import { isForbiddenError } from '@/utils/apiErrors';

/**
 * AppContext provides the current appId, tenantId, and app object.
 * This eliminates props drilling and redundant API calls across the component tree.
 *
 * Usage:
 * 1. Wrap app routes with <AppProvider> at the layout level
 * 2. Access appId, tenantId, and app in any child component with useApp()
 *
 * Example:
 * ```tsx
 * const { appId, tenantId, app } = useApp();
 * ```
 */

interface AppContextValue {
  appId: string;
  tenantId: string;
  app: Application;
  isLoading: boolean;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

/**
 * AppProvider extracts appId from route params, fetches app data, and provides all via context.
 * Should be placed at the app layout level (wraps routes with :appId param).
 *
 * This eliminates redundant useGetApplicationQuery calls across page components.
 */
export const AppProvider = ({ children }: AppProviderProps) => {
  const { appId } = useParams<{ appId: string }>();

  // Fetch app data once at provider level
  const { data: app, isLoading, error } = useGetApplicationQuery(appId!, { skip: !appId });

  // Verify the user has permissions for this workspace (returns 403 if not)
  const { error: permissionsError, isLoading: permissionsLoading } = useGetWorkspaceUsersQuery(
    { tenantId: app?.tenantId ?? '', appId: appId! },
    { skip: !appId || !app?.tenantId }
  );

  if (!appId) {
    throw new Error('AppProvider requires :appId route parameter');
  }

  if (isLoading || permissionsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    (error && isForbiddenError(error)) ||
    (permissionsError && isForbiddenError(permissionsError))
  ) {
    return <AccessDenied />;
  }

  if (error || !app) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load application. Please check your permissions and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <AppContext.Provider value={{ appId, tenantId: app.tenantId, app, isLoading: false }}>
      {children}
    </AppContext.Provider>
  );
};

/**
 * Hook to access the current appId from context.
 * Must be used within an AppProvider.
 *
 * @throws {Error} If used outside of AppProvider
 */
export const useApp = (): AppContextValue => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
};
